import { useSelector } from "react-redux";
import { orderBy } from 'lodash';

// Const
import { userType } from "const/nuclias/user";
import { deviceType, deviceConfig } from 'const/nuclias/device';
import { organizationType } from "const/nuclias/organization";

// Slice
import { selectOrgLevel, selectOrg } from "stores/slice/pageContext";

// Hook
import useUser from "./useUser";

const usePageContext = () => {
  const { user } = useUser();
  const pageContext = useSelector(state => state.pageContext);
  const orgTreeMap = useSelector(state => state.pageContext.orgTreeMap);
  const isOrgLevel = useSelector(selectOrgLevel) === organizationType.org ? true : false;
  const selectedOrg = useSelector(selectOrg(isOrgLevel));
  const selectedSiteTag = useSelector(state => state.pageContext.selectedStoreTag);
  const selectedSite = useSelector(state => state.pageContext.selectedStore);

  const getAccessLevel = () => {
    let accessLevel = null;

    if (
      (user.type === userType.si && selectedOrg.orgType === organizationType.org) ||
      (user.type === userType.org)
    ) {
      accessLevel = organizationType.org;
    }

    if (user.type === userType.siteTag) {
      accessLevel = organizationType.tag;
    }

    if (user.type === userType.site) {
      accessLevel = organizationType.site;
    }

    return accessLevel;
  }

  const getManagedSiteTagList = () => {
    const list = [];

    if (
      (user.type === userType.si && selectedOrg.orgType === organizationType.org) ||
      (user.type === userType.org)
    ) {
      for (const key in selectedOrg.storeTagObj) {
        const { id, name } = selectedOrg.storeTagObj[key];
        list.push({ id, name });
      }
    }

    if (user.type === userType.siteTag) {
      list.push(selectedSiteTag);
      for (const key in selectedSiteTag.storeTagObj) {
        const { id, name } = selectedSiteTag.storeTagObj[key];
        list.push({ id, name });
      }
    }

    return list;
  }

  const getManagedSiteList = () => {
    const list = [];

    if (
      (user.type === userType.si && selectedOrg.orgType === organizationType.org) ||
      (user.type === userType.org)
    ) {
      for (const key in selectedOrg.storeObj) {
        const { id, name } = selectedOrg.storeObj[key];
        list.push({ id, name });
      }
    }

    if (user.type === userType.siteTag) {
      for (const key in selectedSiteTag.storeObj) {
        const { id, name } = selectedSiteTag.storeObj[key];
        list.push({ id, name });
      }
    }

    if (user.type === userType.site) {
      list.push({
        id: selectedSite.id,
        name: selectedSite.name
      });
    }

    return list;
  }

  const getUnusedDeviceListByUserWithManagedOrg = () => {
    const checkIsCloudDevice = (type) => {
      const acceptableDeviceTypeList = [deviceType.ap, deviceType.switch, deviceType.gateway]; // Exclude SSO
      return acceptableDeviceTypeList.indexOf(type);
    }

    const list = [];

    for (const deviceId in selectedOrg.deviceObj) {
      const device = selectedOrg.deviceObj[deviceId];

      if (
        device.registrationState === deviceConfig.unregistered &&
        checkIsCloudDevice(device.deviceModel.deviceType) > -1
      ) {
        list.push({
          uid: device.uid,
          name: device.name,
          title: device.name
        });
      }
    }

    return orderBy(list);
  }

  const getProfileListByUserWithManagedOrg = () => {
    const { type: userIdentity } = user;

    const list = [];

    let profileSource = {};

    if (userIdentity === userType.org) {
      profileSource = selectedOrg.deviceGroupObj;
    }

    if (userIdentity === userType.siteTag) {
      let siteTagIdList = [selectedSiteTag.id];
      let siteIdList = [];

      for (const siteTagId in selectedSiteTag.storeTagObj) {
        siteTagIdList.push(+siteTagId);
      }

      for (const siteId in orgTreeMap.withStore) {
        for (const siteTagId of siteTagIdList) {
          if (orgTreeMap.withStore[siteId].indexOf(siteTagId) > -1) {
            siteIdList.push(+siteId);
          }
        }
      }

      for (let profileId in selectedOrg.deviceGroupObj) {
        const profile = selectedOrg.deviceGroupObj[profileId];
        for (const siteTagId of siteTagIdList) {
          if (profile.storeTagId === siteTagId) {
            profileSource[profile.id] = profile;
          }
        }

        for (const siteId of siteIdList) {
          if (profile.storeId === siteId) {
            profileSource[profile.id] = profile;
          }
        }
      }
    }

    if (userIdentity === userType.site) {
      for (let index in selectedSite.deviceGroups) {
        const profile = selectedSite.deviceGroups[index];
        profileSource[profile.id] = profile;
      }
    }

    for (const profileId in profileSource) {
      const profile = selectedOrg.deviceGroupObj[profileId];

      list.push({
        id: profile.id,
        name: profile.name,
        title: profile.name,
        deviceModel: profile.deviceModel.name
      });
    }

    return orderBy(list, ['title', 'asc']);
  }

  const getSiteListByUserWithManagedOrg = () => {
    const list = [];

    for (const siteId in selectedOrg.storeObj) {
      const site = selectedOrg.storeObj[siteId];

      list.push({
        id: site.id,
        name: site.name,
        title: site.name,
        restrictCountryCode: deviceConfig.lockStC[site.address.country] || null
      });
    }

    return orderBy(list, ['title', 'asc']);
  }

  return {
    pageContext,
    orgTreeMap,
    isOrgLevel,
    selectedOrg,
    selectedSiteTag,
    selectedSite,
    getAccessLevel,
    getManagedSiteTagList,
    getManagedSiteList,
    getUnusedDeviceListByUserWithManagedOrg,
    getProfileListByUserWithManagedOrg,
    getSiteListByUserWithManagedOrg
  };
}

export default usePageContext;